/**
 * skipAdvance.ts — слово владельца 21.10.2026 (задание stylepack/skip-link):
 * переход «без ответа» по ссылке «Пропустить» под кнопкой «Далее».
 *
 * ОДИН ПУТЬ ПЕРЕХОДА, А НЕ ВТОРОЙ. Своего «вперёд без проверки» у визарда не
 * было (автопереход и стрелка «вперёд» шапки зовут тот же `handleNext`, что и
 * главная кнопка, — с проверкой обязательного). Поэтому ссылка зовёт ровно
 * `handleNext` через ту же ссылку `handleNextRef` и ту же паузу пака
 * (`runNav`), а на время вызова взводит признак `skippingRef`. По признаку
 * `handleNext` пропускает три вещи и только их:
 *  · проверку обязательных (`findScreenBlockers`);
 *  · запись ответа: события ответа экрана, отправку почты, ветвление по
 *    выбранному варианту — переход решается как при пустом ответе
 *    (`answersWithoutScreen`);
 *  · значения блоков экрана снимаются (`clearScreenValues`) — «без ответа»
 *    значит: в ответах посетителя для этого экрана ничего не записано.
 * Сторож зацикливания, переход, завершение и редирект — общие с кнопкой.
 */
import { useCallback, useRef, type MutableRefObject } from 'react';
import type { EditorBlock } from '@/store/editorSlice';

type Answers = Record<string, string | string[]>;

/** Ответы без блоков экрана — переход «как при пустом ответе». */
export function answersWithoutScreen(
  answers: Answers,
  blocks: readonly EditorBlock[],
): Answers {
  const own = new Set(blocks.map((b) => b.id));
  return Object.fromEntries(
    Object.entries(answers).filter(([id]) => !own.has(id)),
  );
}

/** Снимает непустые значения блоков экрана. */
export function clearScreenValues(
  blocks: readonly EditorBlock[],
  values: Record<string, string>,
  setValue: (blockId: string, value: string) => void,
): void {
  for (const block of blocks) {
    if (values[block.id]) setValue(block.id, '');
  }
}

/**
 * Обработчик ссылки и признак «идёт переход без ответа». Обработчик стабилен
 * (memo блоков не сбивается, WEB-1993); признак снимается и при сбое.
 */
export function useSkipAdvance(
  handleNextRef: MutableRefObject<() => boolean>,
  runNav: (action: () => boolean | void) => boolean,
): { onSkip: () => void; skippingRef: MutableRefObject<boolean> } {
  const skippingRef = useRef(false);
  const onSkip = useCallback(() => {
    runNav(() => {
      skippingRef.current = true;
      try {
        return handleNextRef.current();
      } finally {
        skippingRef.current = false;
      }
    });
  }, [handleNextRef, runNav]);
  return { onSkip, skippingRef };
}
